import { useEffect, useState } from 'react'

// Findings #146: a Brave / Chrome tab left open keeps running the app it
// loaded, even after a new version is deployed (page loads are network-first,
// but an open tab is never re-loaded). The crew phone stays open all day, so
// it asks the server which version is deployed (`/version.json`, never cached)
// every few minutes and when it comes back online / to the front, and offers
// a reload. Never reloads by itself: a half-typed entry would be lost, and
// saved-but-unsent records are safe in the phone's storage either way.

export const NEW_VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000

/** The version this tab is running; null outside production builds. */
export const currentBuildVersion = (): string | null => (
  typeof __SUNDAY_BUILD_VERSION__ === 'string' && __SUNDAY_BUILD_VERSION__ ? __SUNDAY_BUILD_VERSION__ : null
)

/** The deployed version, or null when it cannot be read (offline, error). */
export const fetchDeployedVersion = async (fetchImpl: typeof fetch = fetch): Promise<string | null> => {
  try {
    const response = await fetchImpl(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return null
    const body = await response.json() as { version?: unknown }
    return typeof body.version === 'string' && body.version ? body.version : null
  } catch {
    return null
  }
}

export const isNewerVersionDeployed = (running: string | null, deployed: string | null): boolean => (
  Boolean(running && deployed && running !== deployed)
)

export const useNewVersionAvailable = (options?: {
  running?: string | null
  fetchVersion?: () => Promise<string | null>
}): boolean => {
  const running = options?.running !== undefined ? options.running : currentBuildVersion()
  const fetchVersion = options?.fetchVersion ?? fetchDeployedVersion
  const [available, setAvailable] = useState(false)

  useEffect(() => {
    if (!running || available) return
    let cancelled = false
    let checking = false
    const check = () => {
      if (checking || (typeof navigator !== 'undefined' && navigator.onLine === false)) return
      checking = true
      void fetchVersion()
        .then((deployed) => {
          if (!cancelled && isNewerVersionDeployed(running, deployed)) setAvailable(true)
        })
        .finally(() => {
          checking = false
        })
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') check()
    }
    check()
    const timer = window.setInterval(check, NEW_VERSION_CHECK_INTERVAL_MS)
    window.addEventListener('online', check)
    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      window.removeEventListener('online', check)
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [running, available, fetchVersion])

  return available
}
